import { Sparkles, ChartColumnBig, ChartLine } from 'lucide-vue-next';


import { olapChartTourSteps } from '@/components/olapV2/tour/steps/chart';
import { olapGridTourSteps } from '@/components/olapV2/tour/steps/grid';
import { olapTourSteps } from '@/components/olapV2/tour/steps/olap';
import i18n from '@/plugins/i18n';

const t = (key) => i18n.global.t(key);

// The guided tour is shared by every OLAP V2 page: its steps are anchored to elements common
// to all OLAP pages (olapTourTargets.js), and OlapTour.vue drops any step whose anchor is
// missing on the current page. So OlapPage surfaces the toolbar "?" button on every OLAP page
// with nothing to wire per page.
const DEFAULT_OLAP_TOUR = {
  id: 'olap',
  steps: olapTourSteps,
};

// Per-dataset overrides — for a page that needs a bespoke tour (different steps or a
// dataset-specific intro). Anything not listed here falls back to DEFAULT_OLAP_TOUR.
// e.g. [DATASETS.SOME_SPECIAL_DATASET]: { id: 'some-special', steps: someSpecialSteps },
const OLAP_TOUR_OVERRIDES = {};

export const getOlapTour = (datasetName) => OLAP_TOUR_OVERRIDES[datasetName] || DEFAULT_OLAP_TOUR;

// Chart-block mini-tour (WP-975) — owned by MetricsByDatePanel (the "?" button in its header),
// not dataset-keyed: the chart works the same on every page that renders it. Declared here so
// every tour shares one registry for ids and seen-flag storage.
export const OLAP_CHART_TOUR = {
  id: 'olap-chart',
  steps: olapChartTourSteps,
};

// Grid deep-dive mini-tour (WP-984) — owned by OlapGrid (the "?" button pinned in the
// grouping bar), not dataset-keyed: the grid works the same on every OLAP page. Its
// sidebar-panel steps open/activate the matching tool panel via OlapTour's panel hook.
export const OLAP_GRID_TOUR = {
  id: 'olap-grid',
  steps: olapGridTourSteps,
};

// Ordered list backing the header "Guided tour" modal (WP-987). Each entry pairs a tour id
// with display metadata (Lucide icon + title/description). title/description are getters so
// they resolve against the active language on every read — same reason as the step files
// (a plain t('...') value would freeze at import time). The modal renders these and asks the
// tour launcher (state/olapTourLauncher.js) to start the tour on the mounted OLAP host; the
// tours themselves are unchanged. "Export and Sharing" (the 4th mockup row) has no tour yet
// and is intentionally omitted until it is authored.
export const GUIDED_TOURS = [
  {
    id: DEFAULT_OLAP_TOUR.id,
    icon: Sparkles,
    get title() { return t('tour.olap.menu.title'); },
    get description() { return t('tour.olap.menu.description'); },
  },
  {
    id: OLAP_CHART_TOUR.id,
    icon: ChartColumnBig,
    get title() { return t('tour.olapChart.menu.title'); },
    get description() { return t('tour.olapChart.menu.description'); },
  },
  {
    id: OLAP_GRID_TOUR.id,
    icon: ChartLine,
    get title() { return t('tour.olapGrid.menu.title'); },
    get description() { return t('tour.olapGrid.menu.description'); },
  },
];

// "Seen" is a light per-browser nudge (localStorage), not a hard gate: the "?" button is
// always available, we only drop the unseen dot once the user has opened the tour once.
const seenKey = (tourId) => `wp.olapTour.seen.${tourId}`;

export const isOlapTourSeen = (tourId) => {
  try {
    return localStorage.getItem(seenKey(tourId)) === '1';
  } catch {
    return false;
  }
};

export const markOlapTourSeen = (tourId) => {
  try {
    localStorage.setItem(seenKey(tourId), '1');
  } catch {
    // private mode / storage disabled — the tour simply keeps its unseen dot next time
  }
};
